import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { notificationsService } from '@/services/notifications';
import { useAuthStore } from '@/stores/auth';

export const useNotificationsStore = defineStore('notifications', () => {
  // Initialize state from localStorage if available
  const storedNotifications = localStorage.getItem('notifications');
  const storedUnreadCount = localStorage.getItem('unreadCount');

  const notifications = ref(storedNotifications ? JSON.parse(storedNotifications) : []);
  const unreadCount = ref(storedUnreadCount ? parseInt(storedUnreadCount) : 0);
  const isLoading = ref(false);
  const error = ref(null);
  const authStore = useAuthStore();
  const initialized = ref(false);
  let fetchPromise = null;

  // Watch auth state and initialization
  watch(
    () => ({
      isAuthenticated: authStore.isAuthenticated,
      initialized: authStore.initialized
    }),
    (newState) => {
      if (newState.initialized && !newState.isAuthenticated) {
        clearNotifications();
      }
    },
    { immediate: true, deep: true }
  );

  // Watch notifications for changes and persist to localStorage
  watch(notifications, (newVal) => {
    if (authStore.isAuthenticated) {
      localStorage.setItem('notifications', JSON.stringify(newVal));
    }
  }, { deep: true });

  // Watch unreadCount for changes and persist to localStorage
  watch(unreadCount, (newVal) => {
    if (authStore.isAuthenticated) {
      localStorage.setItem('unreadCount', newVal.toString());
    }
  });

  function clearNotifications() {
    notifications.value = [];
    unreadCount.value = 0;
    error.value = null;
    initialized.value = false;
    fetchPromise = null;
    localStorage.removeItem('notifications');
    localStorage.removeItem('unreadCount');
  }

  function updateUnreadCount() {
    unreadCount.value = notifications.value?.filter(n => !n.read).length || 0;
  }

  async function fetchNotifications() {
    if (!authStore.isAuthenticated) {
      return [];
    }

    if (fetchPromise) {
      return fetchPromise;
    }

    try {
      isLoading.value = true;
      error.value = null;
      
      fetchPromise = notificationsService.getNotifications();
      const data = await fetchPromise;
      
      if (Array.isArray(data)) {
        notifications.value = data;
        updateUnreadCount();
      } else {
        console.error('Invalid notifications data received');
        error.value = 'Failed to load notifications. Please try again.';
        notifications.value = [];
      }
      return data;
    } catch (err) {
      console.error('Error fetching notifications:', err);
      error.value = 'Failed to load notifications. Please try again.';
      if (err.response?.status === 401) {
        clearNotifications();
      } else {
        notifications.value = [];
      }
      throw err;
    } finally {
      isLoading.value = false;
      fetchPromise = null;
    }
  }

  async function fetchAllNotifications() {
    if (!authStore.initialized || !authStore.isAuthenticated) {
      return;
    }

    if (fetchPromise) {
      return fetchPromise;
    }

    try {
      isLoading.value = true;
      error.value = null;
      
      fetchPromise = notificationsService.getAllNotifications();
      const data = await fetchPromise;
      
      if (Array.isArray(data)) {
        notifications.value = data;
        updateUnreadCount();
      } else {
        console.error('Invalid notifications data received');
        error.value = 'Failed to load notifications. Please try again.';
        notifications.value = [];
      }
      return data;
    } catch (err) {
      console.error('Error fetching all notifications:', err);
      error.value = 'Failed to load notifications. Please try again.';
      if (err.response?.status === 401) {
        clearNotifications();
      } else {
        notifications.value = [];
      }
      throw err;
    } finally {
      isLoading.value = false;
      fetchPromise = null;
    }
  }

  async function markAsRead(notificationId) {
    if (!authStore.isAuthenticated) return;

    try {
      error.value = null;
      await notificationsService.markAsRead(notificationId);
      
      const notification = notifications.value.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        notification.read = true;
        updateUnreadCount();
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
      error.value = 'Failed to mark notification as read';
      if (err.response?.status === 401) {
        notifications.value = [];
        unreadCount.value = 0;
      }
    }
  }

  async function markAllAsRead() {
    if (!authStore.isAuthenticated) return;

    try {
      error.value = null;
      await notificationsService.markAllAsRead();
      notifications.value.forEach(notification => {
        notification.read = true;
      });
      updateUnreadCount();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      error.value = 'Failed to mark all notifications as read';
      if (err.response?.status === 401) {
        notifications.value = [];
        unreadCount.value = 0;
      }
    }
  }

  async function refresh() {
    const path = window.location.pathname;
    if (path === '/notifications') {
      await fetchAllNotifications();
    } else {
      await fetchNotifications();
    }
  }

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    initialized,
    fetchNotifications,
    fetchAllNotifications,
    markAsRead,
    markAllAsRead,
    refresh,
    clearNotifications
  };
}); 